import { useMemo, useState, type ReactElement } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionList,
  PageHeader,
  SelectField,
  useToast,
  type SelectOption,
} from '@csa/ui';
import { SUGGESTED_TIME_ZONES, availableTimeZones, zoneAbbreviation } from '@csa/shared';
import { useTimeZone } from '../lib/TimeZoneProvider.js';
import type { SessionResponse } from '../auth/useSession.js';

/**
 * Personal settings. Currently one: the zone dates are displayed in.
 *
 * Behind no permission, and it changes nothing anyone else can see. Both
 * facts are deliberate -- there is no entitlement that could sensibly gate
 * "read times where you live", and a preference that an administrator could
 * set on someone else's behalf would be a preference an auditor has to reason
 * about.
 */
export function SettingsPage({ session }: { session: SessionResponse }): ReactElement {
  const tz = useTimeZone();
  const { toast } = useToast();

  /**
   * `''` is the sentinel for "follow the browser", because an HTML `select`
   * cannot carry `null`. It is converted at the boundary and never leaves
   * this component.
   */
  const [choice, setChoice] = useState<string>(tz.preference ?? '');

  const options = useMemo<SelectOption[]>(() => {
    const now = new Date();
    const label = (zone: string): string => `${zone} (${zoneAbbreviation(now, zone)})`;

    const suggested = SUGGESTED_TIME_ZONES.map((zone) => ({
      value: zone,
      label: label(zone),
    }));

    /**
     * The suggestions first, then everything else alphabetically. A list of
     * six hundred zones in pure alphabetical order puts Africa/Abidjan at
     * the top for an Australian university, which is technically neutral and
     * practically useless.
     */
    const rest = availableTimeZones()
      .filter((zone) => !SUGGESTED_TIME_ZONES.includes(zone))
      .sort((a, b) => a.localeCompare(b))
      .map((zone) => ({ value: zone, label: label(zone) }));

    return [{ value: '', label: `Follow this device (${tz.browserZone})` }, ...suggested, ...rest];
  }, [tz.browserZone]);

  const dirty = choice !== (tz.preference ?? '');

  async function save(): Promise<void> {
    const next = choice === '' ? null : choice;
    try {
      await tz.setPreference(next);
      toast({
        tone: 'success',
        title: 'Time zone saved',
        message:
          next === null
            ? `Dates now follow this device (${tz.browserZone}).`
            : `Dates are now shown in ${next}.`,
      });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not save the time zone',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }

  // A fixed instant, so the two sample renderings below cannot disagree
  // because a second ticked over between them.
  const sample = useMemo(() => new Date(), []);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Settings"
        description="Preferences that affect only how the console looks to you."
      />

      <Card className="flex flex-col gap-4 p-3">
        <header className="flex flex-col gap-1">
          <h2 className="text-sm font-medium text-body">Time zone</h2>
          <p className="text-xs text-muted">
            Every date and time in the console is shown in this zone, and labelled with it.
          </p>
        </header>

        <div className="flex flex-col gap-4">
          <SelectField
            label="Display dates in"
            value={choice}
            onChange={(event) => setChoice(event.target.value)}
            options={options}
            description={
              tz.isFollowingBrowser
                ? 'Detected from this device. Change it if you work in a different zone from the one your computer is set to.'
                : 'Overriding this device. Choose “Follow this device” to go back to detection.'
            }
          />

          <div className="flex items-center gap-3">
            <Button onClick={() => void save()} disabled={!dirty} loading={tz.isSaving}>
              Save
            </Button>
            {dirty ? (
              <Button variant="ghost" onClick={() => setChoice(tz.preference ?? '')}>
                Cancel
              </Button>
            ) : (
              <span className="text-xs text-subtle">Saved</span>
            )}
          </div>

          <DescriptionList
            items={[
              {
                label: 'In use now',
                value: (
                  <span className="flex items-center gap-2">
                    <code className="font-mono text-xs">{tz.timeZone}</code>
                    {tz.isFollowingBrowser ? <Badge tone="neutral">detected</Badge> : null}
                  </span>
                ),
              },
              {
                label: 'This device reports',
                value: <code className="font-mono text-xs">{tz.browserZone}</code>,
              },
              { label: 'Right now, as you will see it', value: tz.format(sample) },
              {
                label: 'The same instant in UTC',
                value: <code className="font-mono text-xs">{sample.toISOString()}</code>,
              },
            ]}
          />
        </div>
      </Card>

      {/*
        Stated on the screen that changes the setting, not only in the
        documentation. Somebody who has just switched their display zone is
        exactly the person about to wonder whether they have changed what the
        audit trail says -- and the answer needs to be somewhere they will
        actually read it.
      */}
      <Alert tone="info" title="What this does not change">
        <ul className="list-disc pl-4 flex flex-col gap-1">
          <li>
            <strong>The record itself.</strong> Times are stored as instants, not as wall-clock
            readings. Changing this changes how they are drawn, and nothing else.
          </li>
          <li>
            <strong>Exports.</strong> Evidence packs and CSV downloads are always UTC ISO-8601,
            whoever asks for them. Two people exporting the same period get identical files.
          </li>
          <li>
            <strong>What anyone else sees.</strong> This is yours alone.
          </li>
        </ul>
        <p className="mt-2">
          Hover any date in the console to see its exact UTC value. The change itself is recorded in
          the audit trail, so a reviewer comparing two accounts of the same event can tell that one
          of you is reading in a different zone.
        </p>
      </Alert>

      <Card className="flex flex-col gap-4 p-3">
        <header className="flex flex-col gap-1">
          <h2 className="text-sm font-medium text-body">Account</h2>
          <p className="text-xs text-muted">From your Entra ID sign-in. Not editable here.</p>
        </header>

        <DescriptionList
          items={[
            { label: 'Name', value: session.user.displayName },
            {
              label: 'Sign-in name',
              value: <code className="font-mono text-xs">{session.user.upn}</code>,
            },
            {
              label: 'Entra object id',
              value: <code className="font-mono text-xs">{session.user.oid}</code>,
            },
          ]}
        />
      </Card>
    </div>
  );
}
